import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import userProfileEndpoints from './userProfileEndpoints';
import { handleUserProfileError } from '../errors';

export interface ChangePasswordCredentials {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ChangePasswordFieldError {
  field: 'currentPassword' | 'newPassword' | 'confirmPassword';
  message: string;
}

export interface ChangePasswordResult {
  success: boolean;
  data?: unknown;
  error?: string;
  fieldErrors?: ChangePasswordFieldError[];
}

export const userProfileChangePassword = async (
  credentials: ChangePasswordCredentials
): Promise<ChangePasswordResult> => {
  try {
    const result = await apiService.postAPI(
      userProfileEndpoints.CHANGE_PASSWORD,
      {
        currentPassword: credentials.currentPassword,
        newPassword: credentials.newPassword,
        confirmPassword: credentials.confirmPassword,
      }
    );

    if (!result.error) {
      return {
        success: true,
        data: result.data,
      };
    } else {
      // Handle error from apiService
      const errorData = result.data;
      const status = result.status;
      const isNetworkError = result.isNetworkError;

      // Extract field-specific errors for validation errors
      const fieldErrors: ChangePasswordFieldError[] = [];
      let generalError: string | undefined;

      if (status === 400 && errorData && typeof errorData === 'object') {
        const apiError = errorData as {
          error?: string;
          message?: string;
          details?: Array<{
            path: string;
            message: string;
            code: string;
          }>;
        };

        // Handle validation errors with details array (Zod schema errors)
        if (apiError.details && Array.isArray(apiError.details)) {
          apiError.details.forEach((detail) => {
            const fieldPath = detail.path.toLowerCase();
            if (
              fieldPath === 'currentpassword' ||
              fieldPath === 'current_password' ||
              detail.path === 'currentPassword'
            ) {
              fieldErrors.push({
                field: 'currentPassword',
                message: detail.message,
              });
            } else if (
              fieldPath === 'newpassword' ||
              fieldPath === 'new_password' ||
              detail.path === 'newPassword'
            ) {
              fieldErrors.push({
                field: 'newPassword',
                message: detail.message,
              });
            } else if (
              fieldPath === 'confirmpassword' ||
              fieldPath === 'confirm_password' ||
              detail.path === 'confirmPassword'
            ) {
              fieldErrors.push({
                field: 'confirmPassword',
                message: detail.message,
              });
            }
          });
        }

        // Handle application logic validation errors (message field)
        // For specific error messages, use the error handler to get mapped messages
        if (apiError.message) {
          const messageLower = apiError.message.toLowerCase();
          // Check for specific error messages that should use mapped error messages
          if (messageLower.includes('current password is incorrect')) {
            const { errorMessage } = handleUserProfileError(
              status,
              errorData,
              isNetworkError
            );
            fieldErrors.push({
              field: 'currentPassword',
              message: errorMessage,
            });
          }
          if (
            messageLower.includes('user not found') ||
            messageLower.includes('password does not meet requirements') ||
            messageLower.includes('cannot reuse last 5 passwords')
          ) {
            const { errorMessage } = handleUserProfileError(
              status,
              errorData,
              isNetworkError
            );
            generalError = errorMessage;
          } else if (fieldErrors.length === 0) {
            generalError = apiError.message;
          }
        }

        // Handle error field (fallback)
        if (apiError.error && !generalError && fieldErrors.length === 0) {
          generalError = apiError.error;
        }
      }

      // Handle 401 errors (Unauthorized)
      if (status === 401) {
        const { errorMessage } = handleUserProfileError(
          status,
          errorData,
          isNetworkError
        );
        generalError = errorMessage;
      }

      // Handle 403 errors (Forbidden - Account deactivated)
      if (status === 403) {
        const { errorMessage } = handleUserProfileError(
          status,
          errorData,
          isNetworkError
        );
        generalError = errorMessage;
      }

      // Handle 500 errors (Server error)
      if (status === 500) {
        const { errorMessage } = handleUserProfileError(
          status,
          errorData,
          isNetworkError
        );
        generalError = errorMessage;
      }

      // If no field-specific errors and no general error, use centralized error handler
      if (fieldErrors.length === 0 && !generalError) {
        const { errorMessage } = handleUserProfileError(
          status,
          errorData,
          isNetworkError
        );
        generalError = errorMessage;
      }

      return {
        success: false,
        ...(generalError && { error: generalError }),
        ...(fieldErrors.length > 0 && { fieldErrors }),
      };
    }
  } catch (error) {
    // Handle unexpected errors using centralized error handler
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      // Extract field-specific errors if available
      const fieldErrors: ChangePasswordFieldError[] = [];
      let generalError: string | undefined;

      if (status === 400 && errorData && typeof errorData === 'object') {
        const apiError = errorData as {
          error?: string;
          message?: string;
          details?: Array<{
            path: string;
            message: string;
            code: string;
          }>;
        };

        // Handle validation errors with details array
        if (apiError.details && Array.isArray(apiError.details)) {
          apiError.details.forEach((detail) => {
            const fieldPath = detail.path.toLowerCase();
            // Handle camelCase, snake_case, and lowercase variations
            if (
              fieldPath === 'currentpassword' ||
              fieldPath === 'current_password' ||
              detail.path === 'currentPassword'
            ) {
              fieldErrors.push({
                field: 'currentPassword',
                message: detail.message,
              });
            } else if (
              fieldPath === 'newpassword' ||
              fieldPath === 'new_password' ||
              detail.path === 'newPassword'
            ) {
              fieldErrors.push({
                field: 'newPassword',
                message: detail.message,
              });
            } else if (
              fieldPath === 'confirmpassword' ||
              fieldPath === 'confirm_password' ||
              detail.path === 'confirmPassword'
            ) {
              fieldErrors.push({
                field: 'confirmPassword',
                message: detail.message,
              });
            }
          });
        }

        // Handle application logic validation errors
        // For specific error messages, use the error handler to get mapped messages
        if (apiError.message) {
          const messageLower = apiError.message.toLowerCase();
          if (
            messageLower.includes('user not found') ||
            messageLower.includes('current password is incorrect') ||
            messageLower.includes('password does not meet requirements') ||
            messageLower.includes('cannot reuse last 5 passwords')
          ) {
            // Use error handler to get the mapped custom message
            const { errorMessage } = handleUserProfileError(
              status,
              errorData,
              isNetworkError
            );
            generalError = errorMessage;
          } else if (fieldErrors.length === 0) {
            generalError = apiError.message;
          }
        }
      }

      // If no field-specific errors, use centralized error handler
      if (fieldErrors.length === 0 && !generalError) {
        const { errorMessage } = handleUserProfileError(
          status,
          errorData,
          isNetworkError
        );
        generalError = errorMessage;
      }

      // Ensure we always have an error message if no field errors
      if (!generalError && fieldErrors.length === 0) {
        generalError = 'An unexpected error occurred. Please try again.';
      }

      const result: ChangePasswordResult = {
        success: false,
      };

      if (generalError) {
        result.error = generalError;
      }

      if (fieldErrors.length > 0) {
        result.fieldErrors = fieldErrors;
      }

      return result;
    }

    // Handle non-Axios errors
    const { errorMessage } = handleUserProfileError(
      undefined,
      error instanceof Error ? error.message : String(error),
      false
    );

    return {
      success: false,
      error: errorMessage,
    };
  }
};
